/**
 * Hooks para gestión financiera
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  chargeService,
  paymentService,
  expenseService,
  subscribeToChargesByClubAndPeriod,
  subscribeToChargesByClub,
  subscribeToPendingCharges,
  subscribeToChargesByMember,
  subscribeToPaymentsByClubAndPeriod,
  subscribeToPaymentsByClub,
  subscribeToPaymentsByMember,
  subscribeToExpensesByClubAndPeriod,
  subscribeToExpensesByClub,
  subscribeToFinancialSummary,
  subscribeToChargesByPerson,
  subscribeToPaymentsByCharge,
  subscribeToPaymentsByPerson,
} from "@/lib/services/finance";
import type { Charge, Payment, Expense } from "@/types";
import type { FinancialSummary } from "@/lib/services/finance";
import { useAuthStore } from "@/stores/authStore";
import { useRealtimeList } from "@/hooks/useRealtime";

// ==================== QUERY KEYS ====================

export const financeKeys = {
  all: ["finance"] as const,
  charges: () => [...financeKeys.all, "charges"] as const,
  chargesByClub: (clubId: string) =>
    [...financeKeys.charges(), "club", clubId] as const,
  chargesByClubPeriod: (clubId: string, periodId: string) =>
    [...financeKeys.charges(), "club", clubId, "period", periodId] as const,
  chargesByPerson: (personId: string) =>
    [...financeKeys.charges(), "person", personId] as const,
  chargesByMember: (memberId: string) =>
    [...financeKeys.charges(), "member", memberId] as const,
  pendingCharges: (clubId: string) =>
    [...financeKeys.charges(), "pending", clubId] as const,
  chargeDetail: (id: string) => [...financeKeys.charges(), id] as const,

  payments: () => [...financeKeys.all, "payments"] as const,
  paymentsByClub: (clubId: string) =>
    [...financeKeys.payments(), "club", clubId] as const,
  paymentsByClubPeriod: (clubId: string, periodId: string) =>
    [...financeKeys.payments(), "club", clubId, "period", periodId] as const,
  paymentsByCharge: (chargeId: string) =>
    [...financeKeys.payments(), "charge", chargeId] as const,
  paymentsByPerson: (personId: string) =>
    [...financeKeys.payments(), "person", personId] as const,
  paymentsByMember: (memberId: string) =>
    [...financeKeys.payments(), "member", memberId] as const,

  expenses: () => [...financeKeys.all, "expenses"] as const,
  expensesByClub: (clubId: string) =>
    [...financeKeys.expenses(), "club", clubId] as const,
  expensesByClubPeriod: (clubId: string, periodId: string) =>
    [...financeKeys.expenses(), "club", clubId, "period", periodId] as const,

  summary: (clubId: string, periodId?: string) =>
    [...financeKeys.all, "summary", clubId, periodId || "all"] as const,
};

// ==================== CHARGES HOOKS ====================

export function useChargesByClub(clubId: string | undefined) {
  return useRealtimeList<Charge>(
    clubId ? (cb, onErr) => subscribeToChargesByClub(clubId, cb, onErr) : null,
    [clubId],
  );
}

export function useChargesByClubAndPeriod(
  clubId: string | undefined,
  periodId: string | undefined,
) {
  return useRealtimeList<Charge>(
    clubId && periodId
      ? (cb, onErr) =>
          subscribeToChargesByClubAndPeriod(clubId, periodId, cb, onErr)
      : null,
    [clubId, periodId],
  );
}

export function useChargesByPerson(personId: string | undefined) {
  return useRealtimeList<Charge>(
    personId
      ? (cb, onErr) => subscribeToChargesByPerson(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function useChargesByMember(memberId: string | undefined) {
  return useRealtimeList<Charge>(
    memberId
      ? (cb, onErr) => subscribeToChargesByMember(memberId, cb, onErr)
      : null,
    [memberId],
  );
}

export function usePendingCharges(clubId: string | undefined) {
  return useRealtimeList<Charge>(
    clubId ? (cb, onErr) => subscribeToPendingCharges(clubId, cb, onErr) : null,
    [clubId],
  );
}

export function useCharge(id: string | undefined) {
  return useQuery({
    queryKey: financeKeys.chargeDetail(id!),
    queryFn: () => chargeService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateCharge() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Charge,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => chargeService.create(data, user?.uid || ""),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.chargesByClub(variables.clubId),
      });
      queryClient.invalidateQueries({
        queryKey: financeKeys.summary(variables.clubId),
      });
      toast.success("Cobro creado correctamente");
    },
    onError: () => {
      toast.error("Error al crear el cobro");
    },
  });
}

export function useUpdateCharge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Charge>;
      clubId: string;
    }) => chargeService.update(id, data),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.chargesByClub(clubId),
      });
      queryClient.invalidateQueries({ queryKey: financeKeys.summary(clubId) });
      toast.success("Cobro actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el cobro");
    },
  });
}

// ==================== PAYMENTS HOOKS ====================

export function usePaymentsByClub(clubId: string | undefined) {
  return useRealtimeList<Payment>(
    clubId ? (cb, onErr) => subscribeToPaymentsByClub(clubId, cb, onErr) : null,
    [clubId],
  );
}

export function usePaymentsByClubAndPeriod(
  clubId: string | undefined,
  periodId: string | undefined,
) {
  return useRealtimeList<Payment>(
    clubId && periodId
      ? (cb, onErr) =>
          subscribeToPaymentsByClubAndPeriod(clubId, periodId, cb, onErr)
      : null,
    [clubId, periodId],
  );
}

export function usePaymentsByCharge(chargeId: string | undefined) {
  return useRealtimeList<Payment>(
    chargeId
      ? (cb, onErr) => subscribeToPaymentsByCharge(chargeId, cb, onErr)
      : null,
    [chargeId],
  );
}

export function usePaymentsByPerson(personId: string | undefined) {
  return useRealtimeList<Payment>(
    personId
      ? (cb, onErr) => subscribeToPaymentsByPerson(personId, cb, onErr)
      : null,
    [personId],
  );
}

export function usePaymentsByMember(memberId: string | undefined) {
  return useRealtimeList<Payment>(
    memberId
      ? (cb, onErr) => subscribeToPaymentsByMember(memberId, cb, onErr)
      : null,
    [memberId],
  );
}

export function useCreatePayment() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Payment,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => paymentService.create(data, user?.uid || ""),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.paymentsByClub(variables.clubId),
      });
      queryClient.invalidateQueries({
        queryKey: financeKeys.chargesByClub(variables.clubId),
      });
      queryClient.invalidateQueries({
        queryKey: financeKeys.summary(variables.clubId),
      });
      queryClient.invalidateQueries({
        queryKey: financeKeys.paymentsByCharge(variables.chargeId),
      });
      toast.success("Pago registrado correctamente");
    },
    onError: () => {
      toast.error("Error al registrar el pago");
    },
  });
}

export function useReversePayment() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: ({
      id,
      reason,
      clubId: _clubId,
    }: {
      id: string;
      reason: string;
      clubId: string;
    }) => paymentService.reverse(id, reason, user?.uid || ""),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.paymentsByClub(clubId),
      });
      queryClient.invalidateQueries({
        queryKey: financeKeys.chargesByClub(clubId),
      });
      queryClient.invalidateQueries({ queryKey: financeKeys.summary(clubId) });
      toast.success("Pago reversado");
    },
    onError: () => {
      toast.error("Error al reversar el pago");
    },
  });
}

// ==================== EXPENSES HOOKS ====================

export function useExpensesByClub(clubId: string | undefined) {
  return useRealtimeList<Expense>(
    clubId ? (cb, onErr) => subscribeToExpensesByClub(clubId, cb, onErr) : null,
    [clubId],
  );
}

export function useExpensesByClubAndPeriod(
  clubId: string | undefined,
  periodId: string | undefined,
) {
  return useRealtimeList<Expense>(
    clubId && periodId
      ? (cb, onErr) =>
          subscribeToExpensesByClubAndPeriod(clubId, periodId, cb, onErr)
      : null,
    [clubId, periodId],
  );
}

export function useCreateExpense() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: (
      data: Omit<
        Expense,
        "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
      >,
    ) => expenseService.create(data, user?.uid || ""),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.expensesByClub(variables.clubId),
      });
      queryClient.invalidateQueries({
        queryKey: financeKeys.summary(variables.clubId),
      });
      toast.success("Gasto registrado correctamente");
    },
    onError: () => {
      toast.error("Error al registrar el gasto");
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<Expense>;
      clubId: string;
    }) => expenseService.update(id, data),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.expensesByClub(clubId),
      });
      queryClient.invalidateQueries({ queryKey: financeKeys.summary(clubId) });
      toast.success("Gasto actualizado");
    },
    onError: () => {
      toast.error("Error al actualizar el gasto");
    },
  });
}

export function useCancelExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, clubId: _clubId }: { id: string; clubId: string }) =>
      expenseService.cancel(id),
    onSuccess: (_, { clubId }) => {
      queryClient.invalidateQueries({
        queryKey: financeKeys.expensesByClub(clubId),
      });
      queryClient.invalidateQueries({ queryKey: financeKeys.summary(clubId) });
      toast.success("Gasto cancelado");
    },
    onError: () => {
      toast.error("Error al cancelar el gasto");
    },
  });
}

// ==================== SUMMARY HOOK ====================

export function useFinancialSummary(
  clubId: string | undefined,
  periodId?: string,
) {
  const [data, setData] = useState<FinancialSummary | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!clubId) {
      setData(undefined);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const unsub = subscribeToFinancialSummary(
      clubId,
      periodId,
      (summary) => {
        setData(summary);
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      },
    );
    return unsub;
  }, [clubId, periodId]);

  return { data, isLoading, error };
}
